import { AppBar, Box, Breadcrumbs, Chip, Container, Divider, Slide, Tab, Tabs, Tooltip, Typography, useScrollTrigger } from '@mui/material'
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { isMobile } from '../../../helpers/deviceDetect';
import Link from 'next/link';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import ReplyIcon from '@mui/icons-material/Reply';
import ShareThis from '../ShareThis';

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  return {
    height: '24px',
    backgroundColor: 'transparent',
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': {
      // backgroundColor: 'rgba(23, 76, 134, .1)',
      // cursor: 'pointer',
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[1],
      color: theme.palette.text.color.white,
      icon: {
        color: theme.palette.text.link,
      }
    },
    '&:active': {
      boxShadow: theme.shadows[2],
      // backgroundColor: theme.palette.background.primary,
    },
  };
});

function ShowAppBarOnScroll(props) {
  const { children, window } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const trigger = typeof window !== 'undefined' ? useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 100,
  }) : false;

  return (
    <Slide direction="down" in={trigger}>
      <Box
        // onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', top: 54, left: 0, bgcolor: 'background.paper', zIndex: 99 }}
      >
        {children}
      </Box>
    </Slide>
  );
}

ShowAppBarOnScroll.propTypes = {
  children: PropTypes.element.isRequired,
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
};

function HideOnScroll(props) {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction="down" in={trigger}>
      {children}
    </Slide>
  );
}

HideOnScroll.propTypes = {
  children: PropTypes.element.isRequired,
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
};

const EcoTabs = styled(Tabs)(({ theme }) => ({
  // borderBottom: '1px solid #e8e8e8',
  '& .MuiTabs-indicator': {
    // backgroundColor: '#1890ff',
    height: '3px',
    backgroundColor: theme.palette.text.link,
  },
}));

const EcoTab = styled((props) => <Tab disableRipple {...props} component="a" />)(({ theme }) => ({
  textTransform: 'capitalize',
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    minWidth: 0,
  },
  // fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  color: '#333',
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  '&:hover': {
    // color: '#40a9ff',
    // color: theme.palette.text.link,
    color: theme.palette.text.color.white,
    opacity: 1,
  },
  color: 'text.description',
  '&.Mui-selected': {
    color: '#333',
    fontWeight: 600,
    // color: theme.palette.text.link,
    // fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#d1eaff',
  },
}));

const EconomyToolbar = (props) => {
  const { themeMode, value, handleChange, tabName, tabHead } = props || {}
  const router = useRouter();
  const { asPath } = router;
  const [clipboard, setClipboard] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const handleClipboard = (value) => {
    const el = document.createElement('textarea');
    el.value = value;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    setClipboard("Copied");

    setTimeout(() => {
      setClipboard(null);
    }, 3000);
  };

  return (
    <>
      <Box sx={{ background: '#f8f9fa', pt: 2, boxShadow: '0 0.5px 1px rgb(132 135 154 / 20%), 0 2px 3px rgb(132 135 154 / 10%)' }}>
        <Container maxWidth="lg">
          <AppBar position="static" sx={{ borderRadius: '5px', top: 0, bgcolor: 'transparent' }} elevation={0}>
            <Box component="div" className="fund-info p-0" >
              <Box className='eco'
                sx={{
                  display: 'flex', alignItems: 'end',
                  '& .hyper-link': { display: 'none', fontSize: '1.1rem' }, '&:hover .hyper-link': { display: 'inline-block' },
                  '&:hover .hyper-share': { display: 'none' },
                  '&:hover .eco-head': { borderBottom: '1px dotted' }, '& .hyper-link:hover ': { color: '#174c86', cursor: 'pointer' },
                  '& .eco-head': { borderBottom: '1px solid transparent' },
                }}
              >
                <Typography className="eco-head" variant="h1" sx={{ color: 'text.link', fontSize: '1.5rem', fontWeight: '500', m: 2, mb: 0 }}
                  onClick={() => handleClipboard(`${process.env.NEXT_PUBLIC_URL}${asPath}`)}
                >
                  {tabHead || 'US Economy'}
                </Typography>
                <ShareThis
                  link={`${process.env.NEXT_PUBLIC_URL}${asPath}`}
                  title={`View ${tabHead || 'US Economy'} on Grufity`}
                  showButton={false}
                  tooltipText={`Share this page`}
                  showThisButton={<ReplyIcon fontSize="inherit" sx={{ transform: 'rotateY(180deg)', }} />}
                />
                {/* <Tooltip
              title={clipboard ?? "Copy link to this page!"}
              placement="bottom"
            >
              <Box component="div"
                sx={{
                  '& .hyper-share': {
                    transform: 'rotateY(180deg)',
                    color: 'text.very_light_desc', fontSize: '1.2rem',
                  },
                  '& .hyper-link': {
                    color: 'primary.main', fontSize: '1rem',
                  },
                }}
              >
                <ReplyIcon
                  onClick={() => handleClipboard(`${process.env.NEXT_PUBLIC_URL}${asPath}`)}
                  className="hyper-share"
                  // aria-label="share-icon"
                  aria-hidden="true"
                  focusable="false"
                />
                <LinkRoundedIcon
                  onClick={() => handleClipboard(`${process.env.NEXT_PUBLIC_URL}${asPath}`)}
                  className="hyper-link"
                  // aria-label="share-link"
                  aria-hidden="true"
                  focusable="false"
                />
              </Box>
            </Tooltip> */}
              </Box>
            </Box>
            <Box sx={{
              bgcolor: 'transparent', '& .MuiTabs-root': { boxShadow: 0 }, borderRadius: '5px',
              // borderBottom: '1px solid #ebebef',
              '& .MuiButtonBase-root.MuiTabScrollButton-root.Mui-disabled': { opacity: 1 },
              '& .Mui-disabled .MuiSvgIcon-fontSizeSmall': {
                color: 'rgb(156, 156, 156)'
              },
              '& .MuiSvgIcon-fontSizeSmall': {
                color: '#174c86'
              }, mt: 1.5
            }}>
              <EcoTabs
                value={value}
                onChange={handleChange}
                aria-label="us-economy tabs"
                elevation={0}
                centered={false}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <EcoTab label="At a Glance" href="/us-economy/" />
                <EcoTab label="Yield Curve" href="/us-economy/yield-curve" />
                <EcoTab label="GDP" href="/us-economy/us-gdp" />
                <EcoTab label="Housing" href="/us-economy/us-housing-market" />
                <EcoTab label="Consumer" href="/us-economy/us-consumer" />
              </EcoTabs>
            </Box>
          </AppBar>
          {isMobile &&
            <ShowAppBarOnScroll {...props}>
              <Box sx={{ width: '100vw' }}>
                <Box sx={{ bgcolor: 'transparent', '& .MuiTabs-root': { boxShadow: 16 } }}>
                  <EcoTabs
                    value={value}
                    onChange={handleChange}
                    aria-label="us-economy tabs"
                    elevation={0}
                    centered={true}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                  >
                    <EcoTab label="At a Glance" href="/us-economy/" />
                    <EcoTab label="Yield Curve" href="/us-economy/yield-curve" />
                    <EcoTab label="GDP" href="/us-economy/us-gdp" />
                    <EcoTab label="Housing" href="/us-economy/us-housing-market" />
                    <EcoTab label="Consumer" href="/us-economy/us-consumer" />
                  </EcoTabs>
                </Box>
              </Box>
            </ShowAppBarOnScroll>}
        </Container>
        {/* <Divider /> */}
      </Box>
      <Container maxWidth="lg">
        <Box
          sx={{
            padding: '8px',
            mt: 1
          }}
          component="nav" className="bread-container">
          {mounted && (
            <Breadcrumbs maxItems={isMobile ? 2 : 3}>
              <Link prefetch={false} color="inherit" href='/' >
                <StyledBreadcrumb
                  underline="hover"
                  label={"Home"}
                  icon={<HomeRoundedIcon sx={{ color: 'text.link' }} fontSize="small" />}
                  sx={{ color: 'text.secondary', }}
                />
              </Link>
              <StyledBreadcrumb
                label={`US Economy`}
                className={'bread-bg-blue'}
                sx={{ color: 'text.secondary', }}
              />
              <StyledBreadcrumb
                label={tabName}
                sx={{
                  backgroundColor: '#7aa6ba12',
                }}
              />
            </Breadcrumbs>)}
        </Box>
      </Container>
    </>
  )
}

export default React.memo(EconomyToolbar)
